import { type ChangeEvent, type RefObject, useRef, useState } from 'react';
import { type AxiosProgressEvent } from 'axios';
import { Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { useTranslation } from '@/hooks/useTranslation';
import { apiClient } from '@/services/apiClient';
import ImageNotAvailable from '@/assets/image-not-available.png';
import { MediaSource } from '@/types/media';

interface ImageUrlUploadFieldProps {
  compact?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  maxGifSizeBytes?: number;
  onUploadingChange?: (uploading: boolean) => void;
  value: string;
  onChange: (value: string) => void;
  source: MediaSource;
  mediaType?: 'image' | 'video' | 'both';
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  previewAlt?: string;
  showPreview?: boolean;
  showInlinePreview?: boolean;
  previewClassName?: string;
  previewContainerClassName?: string;
  size?: 'md' | 'sm';
}

interface MediaUploadApiResponse {
  success: boolean;
  url?: string;
}

const ImageUrlUploadField = ({
  value,
  compact = false,
  inputRef,
  onUploadingChange,
  maxGifSizeBytes,
  onChange,
  source,
  mediaType = 'image',
  label,
  placeholder,
  error,
  disabled = false,
  previewAlt = 'Vista previa',
  showPreview = true,
  showInlinePreview = false,
  previewClassName = 'h-58 w-40 object-cover rounded-lg shadow-md',
  previewContainerClassName = 'h-58 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50',
  size = 'md'
}: ImageUrlUploadFieldProps) => {
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = inputRef ?? localInputRef;
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const isVideoValue = /\.(mp4|webm|ogg|mov|m4v|m3u8)(\?|#|$)/i.test(value);
  const fileAccept = mediaType === 'both' ? 'image/*,video/*' : mediaType === 'video' ? 'video/*' : 'image/*';
  const uploadLabel = mediaType === 'both' ? 'Archivo subido' : mediaType === 'video' ? 'Video subido' : 'Imagen subida';
  const invalidTypeLabel = mediaType === 'both'
    ? 'El archivo debe ser una imagen o un video válido.'
    : mediaType === 'video'
      ? 'El archivo debe ser un video válido.'
      : 'El archivo debe ser una imagen válida.';
  const uploadErrorLabel = mediaType === 'both'
    ? 'No se pudo subir el archivo.'
    : mediaType === 'video'
      ? 'No se pudo subir el video.'
      : 'No se pudo subir la imagen.';
  const isSmall = size === 'sm';
  const inputSizeClass = isSmall ? 'px-2 py-1.5 text-base' : 'px-3 py-2';
  const buttonSize = isSmall ? 'xs' : 'md';
  const uploadIconClass = isSmall ? 'w-3.5 h-3.5 mr-1' : 'w-4 h-4 mr-2';

  const openFileDialog = () => {
    if (disabled || uploading) {
      return;
    }
    fileInputRef.current?.click();
  };

  const uploadMediaFile = async (file: File) => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    const isAllowed =
      mediaType === 'both'
        ? isImage || isVideo
        : mediaType === 'video'
          ? isVideo
          : isImage;
    if (!isAllowed) {
      toast.error(invalidTypeLabel);
      return;
    }

    if (maxGifSizeBytes && (file.type === 'image/gif' || /\.gif$/i.test(file.name)) && file.size > maxGifSizeBytes) {
      toast.error(t.richMessage.gifTooLarge);
      return;
    }
    setUploading(true);
    onUploadingChange?.(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', source);

    try {
      const response = await apiClient.post<MediaUploadApiResponse>(
        '/media/upload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (event: AxiosProgressEvent) => {
            if (!event.total) {
              return;
            }
            const progress = Math.round((event.loaded * 100) / event.total);
            setUploadProgress(progress);
          },
        }
      );

      if (response.success && response.url) {
        onChange(response.url);
        toast.success(uploadLabel);
        return;
      }

      toast.error(uploadErrorLabel);
    } catch (uploadError) {
      toast.error(uploadError instanceof Error ? uploadError.message : uploadErrorLabel);
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    void uploadMediaFile(file);
  };

  if (compact) return <div className="flex h-10 items-center gap-2">
    <input ref={fileInputRef} type="file" accept={fileAccept} className="hidden" onChange={handleFileChange} disabled={disabled || uploading} />
    <button type="button" onClick={openFileDialog} disabled={disabled || uploading}
      aria-label={value ? t.richMessage.replaceImage : t.richMessage.addImage}
      className="flex h-10 cursor-pointer items-center gap-2 rounded px-2 text-sm hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-500">
      {value ? <img src={value} alt="" className="h-9 w-12 object-contain" onError={event => {
        if (event.currentTarget.getAttribute('src') !== ImageNotAvailable) event.currentTarget.src = ImageNotAvailable;
      }} /> : <Upload className="h-4 w-4" />}
      {uploading ? `${uploadProgress}%` : value ? null : t.richMessage.addImage}
    </button>
  </div>;

  return (
    <div className={isSmall ? 'space-y-1' : 'space-y-3'}>
      <div className="space-y-1">
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 rounded-md shadow-sm">
            <input
              type="url"
              placeholder={placeholder}
              value={value}
              onChange={(event) => onChange(event.target.value)}
              disabled={disabled || uploading}
              className={`
                min-w-0 flex-1 border border-gray-300 rounded-l-md rounded-r-none border-r-0
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                ${inputSizeClass}
                ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
              `}
            />
            <Button
              type="button"
              variant="outline"
              size={buttonSize}
              onClick={openFileDialog}
              loading={uploading}
              disabled={disabled}
              className="rounded-l-none border-l-0"
            >
              <Upload className={uploadIconClass} />
              {uploading ? `${uploadProgress || 0}%` : 'Subir'}
            </Button>
          </div>
          {showInlinePreview && value && !isVideoValue && (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              aria-label={`${t.imageUpload.openPreview}: ${label || previewAlt}`}
              className="h-10 w-10 shrink-0 overflow-hidden rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <img
                key={value}
                src={value}
                alt={label || previewAlt}
                className="h-full w-full object-contain"
                onError={(event) => {
                  if (event.currentTarget.getAttribute('src') !== ImageNotAvailable) {
                    event.currentTarget.src = ImageNotAvailable;
                  }
                }}
              />
            </button>
          )}
        </div>
        {error && (
          <p className="text-sm text-red-600">
            {error}
          </p>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={fileAccept}
        className="hidden"
        onChange={handleFileChange}
        disabled={disabled || uploading}
      />

      {showInlinePreview && value && !isVideoValue && (
        <Modal
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title={label || t.imageUpload.preview}
          size="2xl"
        >
          <img
            key={value}
            src={value}
            alt={label || previewAlt}
            className="mx-auto max-h-[70vh] max-w-full object-contain"
            onError={(event) => {
              if (event.currentTarget.getAttribute('src') !== ImageNotAvailable) {
                event.currentTarget.src = ImageNotAvailable;
              }
            }}
          />
        </Modal>
      )}

      {showPreview && value ? (
        isVideoValue ? (
          <video
            src={value}
            controls
            className={previewClassName}
          />
        ) : (
          <img
            src={value}
            alt={previewAlt}
            className={previewClassName}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = ImageNotAvailable;
              target.style.display = 'block';
            }}
            onLoad={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'block';
            }}
          />
        )
      ) : showPreview ? (
        <div className={previewContainerClassName}>
          <div className="text-center text-gray-500">
            <div className="text-xs">Vista previa</div>
            <div className="text-xs">
              {mediaType === 'both' ? 'de imagen o video' : mediaType === 'video' ? 'de video' : 'de imagen'}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ImageUrlUploadField;
